import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { v4 as uuid } from 'uuid';
import { selectIsLoadingAPIRequest } from 'store/ApiClient';
import { CalculatorRules, updateCalculatorRulesRequest } from 'store/CalculatorRules';
import { getJobTitlesRequest } from 'store/Collections';
import { selectCurrentEmployeeSite } from 'store/Employers';
import { IStore } from 'store/types';
import { ICalculatorRule, ICalculatorRules, IFormValues, PermissionType, SiteJobSource, TipCalculationsTab, TipCalculatorMethod } from 'types';
import { hasSitePermission } from 'utils/employers';
import { DefaultPoolForm, PointValuesForm, TipSharesForm } from './forms';
import { TabNav } from './TabNav';

interface ITipCalculationsFormProps {
  calculatorRules?: ICalculatorRules;
  activeTab: TipCalculationsTab;
}

const isJobBenefitingFromShare = (jobTitle: string, rules: ICalculatorRule[]) => {
  const title = jobTitle.trim().toLowerCase();
  const isBenefitingFromRules = () => rules.some((r) => r.sharePolicy?.shares.some((s) => s.beneficiaries.some((b) => b.name.trim().toLowerCase() === title)));
  const isBenefitingFromPool = () => rules.some((r) => !r.default && r.sharePolicy?.remainder?.beneficiaries.some((j) => j.participants.some((p) => p.name.trim().toLowerCase() === title)));
  return isBenefitingFromRules() || isBenefitingFromPool();
};

const isJobBenefitingFromDefault = (jobTitle: string, rules: ICalculatorRule[]) => {
  const title = jobTitle.trim().toLowerCase();
  const defaultPool = rules.find((r) => r.default);
  return defaultPool?.sharePolicy.remainder.beneficiaries.some((b) => b.participants.some((p) => p.name.trim().toLowerCase() === title));
};

const getActiveRules = (calculatorRules: ICalculatorRules) => ({
  ...calculatorRules,
  rules:
    calculatorRules?.rules?.map((rule) => ({
      ...rule,
      value: rule.value,
      ...(calculatorRules.method === TipCalculatorMethod.SHARE &&
        !rule.default && {
          isBenefitingFromShare: isJobBenefitingFromShare(rule.contributor.name, calculatorRules.rules),
          isBenefitingFromDefault: isJobBenefitingFromDefault(rule.contributor.name, calculatorRules.rules),
        }),
      id: uuid(),
    })) || [],
  categoryGroups: calculatorRules?.categoryGroups || [],
});

export const TipCalculationsForm: React.FC<ITipCalculationsFormProps> = ({ calculatorRules, activeTab }) => {
  const dispatch = useDispatch();
  const currentSite = useSelector(selectCurrentEmployeeSite);
  const isLoading = useSelector((state: IStore) => selectIsLoadingAPIRequest(state, CalculatorRules.UPDATE_CALCULATOR_RULES_REQUEST));
  const [activeRules, setActiveRules] = useState<IFormValues>({ method: TipCalculatorMethod.NONE, rules: [], categoryGroups: [] });
  const editPermission = hasSitePermission(currentSite?.permissions, PermissionType.CALCULATOR_RULES_UPDATE);

  useEffect(() => {
    setActiveRules(getActiveRules(calculatorRules));
  }, [calculatorRules]);

  const onConfirm = (vals: IFormValues, onSuccess?: () => void, successMessage?: string) => {
    let rules: ICalculatorRule[] = [];
    let method: TipCalculatorMethod;

    if (vals.rules?.length > 0) {
      method = TipCalculatorMethod.SHARE;
      rules = vals.rules.map((rule) => ({
        contributor: rule.contributor && { name: rule.contributor.name, type: rule.contributor.type, providerId: rule.contributor.providerId },
        value: Number(rule.value),
        isVirtual: rule.isVirtual,
        sharePolicy: rule.sharePolicy,
        additionalContributors: rule.additionalContributors,
        default: rule.default,
        includeNoLabor: rule.includeNoLabor,
      }));
    } else {
      method = TipCalculatorMethod.NONE;
    }

    const onCompleted = () => {
      onSuccess?.();
      // refresh job titles
      dispatch(getJobTitlesRequest({ siteId: currentSite.id, sources: [SiteJobSource.POS] }));
    };
    dispatch(updateCalculatorRulesRequest(currentSite.id, { ...vals, method, rules }, { onSuccess: onCompleted }, successMessage));
  };

  let activeForm = <></>;
  const props = {
    siteId: currentSite?.id,
    activeRules,
    isLoading,
    onConfirm,
    editPermission,
    tabNav: <TabNav activeTab={activeTab} />,
  };

  switch (activeTab) {
    case TipCalculationsTab.TIP_SHARES:
      activeForm = <TipSharesForm {...props} />;
      break;
    case TipCalculationsTab.DEFAULT_POOL:
      activeForm = <DefaultPoolForm {...props} />;
      break;
    case TipCalculationsTab.POINT_VALUES:
      activeForm = <PointValuesForm {...props} />;
      break;
    default:
      break;
  }
  return activeForm;
};
